function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useRef, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { fontStyle, spacing, themeLight, radius, breakpoints } from '@naf/theme';
import { propValidationConfig } from '../propValidationConfig';
export const RadioButton = /*#__PURE__*/forwardRef(({
  label,
  name,
  onChange = () => {},
  error = false,
  disabled = false,
  width = 'content',
  outline = false,
  message,
  customColors,
  className,
  ...rest
}, ref) => {
  const radioRef = useRef(null);
  const handleChange = useCallback(() => {
    onChange();
  }, [onChange]);
  const handleRadioClick = useCallback(isActive => {
    if (isActive) {
      handleChange();
      if (!!radioRef && !!radioRef.current) {
        if (!radioRef.current.checked) {
          radioRef.current.checked = true;
        }
      }
    }
  }, [handleChange, radioRef]);
  return /*#__PURE__*/React.createElement(Container, {
    ref: ref,
    outline: outline,
    onClick: () => !disabled && handleRadioClick(outline),
    width: width,
    className: className
  }, /*#__PURE__*/React.createElement(Label, {
    error: error,
    disabled: disabled,
    customColors: customColors
  }, /*#__PURE__*/React.createElement("input", _extends({
    name: name,
    type: "radio",
    tabIndex: -1,
    onChange: () => !disabled && handleChange(),
    disabled: disabled,
    ref: radioRef
  }, rest)), /*#__PURE__*/React.createElement(RadioWrap, null, /*#__PURE__*/React.createElement(StyledRadio, {
    tabIndex: disabled ? -1 : 0,
    onKeyDown: e => {
      if (e.key === 'Enter') {
        handleRadioClick(true);
      }
    }
  })), label ? /*#__PURE__*/React.createElement("span", null, label) : null), message && /*#__PURE__*/React.createElement(Message, {
    error: error,
    disabled: disabled
  }, message));
});
export default RadioButton;
const Container = styled.div.withConfig(propValidationConfig).withConfig({
  displayName: "radio-button__Container",
  componentId: "sc-1smlc3n-0"
})(["display:inline-flex;align-items:flex-start;flex-direction:column;margin:0;width:fit-content;", " ", " ", ";@media (max-width:", "){flex-flow:row wrap;align-items:center;width:calc(100% - 2 * (", ") - ", ");}"], ({
  outline
}) => outline && css(["padding:", ";padding-right:", ";padding-bottom:", ";border-radius:", ";border:1px solid ", ";&:hover{cursor:pointer;input ~ div{border-color:", ";}}"], spacing.space12, spacing.space64, spacing.space16, radius.s, ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default, ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default), ({
  width
}) => width === 'half' && css(["padding-right:0;width:calc(50% - ", ");"], spacing.space12), ({
  width
}) => width === 'full' && css(["padding-right:0;width:calc(100% - ", ");"], spacing.space12), breakpoints.s, spacing.space8, spacing.space12);
const RadioWrap = styled.div.withConfig({
  displayName: "radio-button__RadioWrap",
  componentId: "sc-1smlc3n-1"
})(["display:inline-flex;width:fit-content;height:calc(", " * ", ");align-items:center;@media (max-width:", "){height:calc(", " * ", ");}"], fontStyle.article.articleText['font-size'], fontStyle.article.articleText['line-height'], breakpoints.s, fontStyle.bodyText.bodyText['font-size'], fontStyle.bodyText.bodyText['line-height']);
const StyledRadio = styled.div.withConfig({
  displayName: "radio-button__StyledRadio",
  componentId: "sc-1smlc3n-2"
})(["min-width:", ";width:", ";height:", ";border-radius:50%;box-sizing:border-box;&:active{border-radius:50% !important;}"], spacing.space24, spacing.space24, spacing.space24);
const Label = styled.label.withConfig(propValidationConfig).withConfig({
  displayName: "radio-button__Label",
  componentId: "sc-1smlc3n-3"
})(["display:flex;align-items:flex-start;justify-content:center;height:fit-content;user-select:none;input{opacity:0;position:absolute;z-index:-1000;}&:hover{cursor:pointer;", " ", "}", "{background:", ";border:2px solid ", ";", " ", "}input:checked ~ div > ", "{border:", " solid;border-color:", ";background:", ";", " ", " ", "}span{", ";margin-left:", ";@media (max-width:", "){", ";}}"], ({
  disabled
}) => !disabled && css(["div{border-color:", ";}"], ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default), ({
  disabled
}) => disabled && css(["cursor:not-allowed;"]), StyledRadio, ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  theme
}) => theme.border ? theme.border.heavy : themeLight.border.heavy, ({
  error
}) => error && css(["background:", ";border-color:", ";"], ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default), ({
  disabled
}) => disabled && css(["background:", ";border-color:", ";"], ({
  theme
}) => theme.background ? theme.background.default : themeLight.background.default, ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default), StyledRadio, spacing.space8, ({
  theme
}) => theme.componentColors ? theme.componentColors.interactiveElement.active : themeLight.componentColors.interactiveElement.active, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, ({
  error
}) => error && css(["border-color:", ";background:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error, ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.errorBackground : themeLight.componentColors.alert.errorBackground), ({
  disabled
}) => disabled && css(["border-color:", ";background:", ";"], ({
  theme
}) => theme.border ? theme.border.default : themeLight.border.default, ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText), ({
  customColors
}) => customColors && css(["background:", ";border-color:", ";"], customColors.dotColor, customColors.checkedColor), fontStyle.article.articleText, spacing.space8, breakpoints.s, fontStyle.bodyText.bodyText);
const Message = styled.p.withConfig(propValidationConfig).withConfig({
  displayName: "radio-button__Message",
  componentId: "sc-1smlc3n-4"
})(["", ";line-height:", ";margin:0 0 0 calc(", " + ", ");color:", ";", " ", ";@media (max-width:", "){margin-left:", ";}"], fontStyle.bodyText.small, spacing.space16, spacing.space24, spacing.space8, ({
  theme
}) => theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText, ({
  error
}) => error && css(["color:", ";"], ({
  theme
}) => theme.componentColors ? theme.componentColors.alert.error : themeLight.componentColors.alert.error), ({
  disabled
}) => disabled && css(["color:", ";"], ({
  theme
}) => theme.typography ? theme.typography.disabledText : themeLight.typography.disabledText), breakpoints.s, spacing.space12);
//# sourceMappingURL=radio-button.js.map